import { CartUpdatedEvent, ThemeEvents } from '@theme/events';

/* K&J (29/09/2026, entrée 252) : toute écriture panier passe par la file unique (layout/theme.liquid),
   une requête à la fois, dans l'ordre des clics. Sans la file (page sans le layout) : exécution directe. */
const kjFile = (job) => (window.kjPanierFile ? window.kjPanierFile.run(job) : job());

class QuickAddComponent extends HTMLElement {
  #abortController = null;
  #parentSliderComponent = null;

  constructor() {
    super();

    this.addToCartButton = null;
    this.popupComponent = null;
    this.popupModal = null;
    this.popupToggle = null;
    this.popupModalContent = null;
    this.showProductMediaGallery = null;
  }

  connectedCallback() {
    this.addToCartButton = this.querySelector('[data-ref="add-to-cart-button"]');
    this.popupComponent = this.querySelector('[data-ref="popup-component"]');
    this.popupModal = this.popupComponent?.querySelector('[data-ref="popup-modal"]');
    this.popupToggle = this.popupComponent?.querySelector('[data-ref="popup-toggle"]');
    this.popupModalContent = this.popupComponent?.querySelector('[data-ref="popup-content"]');
    this.showProductMediaGallery = this.popupComponent?.dataset.showProductMediaGallery;
    this.#parentSliderComponent = this.closest('slider-component');

    if (this.#isGiftOnly()) return;

    this.addToCartButton?.addEventListener('click', this.#handleAddToCart);
    this.popupToggle?.addEventListener('click', this.#handlePopupToggle);
    this.popupModal?.addEventListener('close', this.#handlePopupClose);

    /* KJ-B2B (25/09) : sélecteur de quantité « vivant » (quantité réelle du panier, 0 = retiré) */
    this.kjRow = this.querySelector('[data-ref="kj-row"]');
    this.kjQty = this.querySelector('[data-ref="kj-cart-qty"]');
    this.kjQty?.addEventListener('change', this.#kjHandleQtyChange);

    document.addEventListener(ThemeEvents.cartUpdated, this.#handleCartUpdate);
    document.addEventListener('kj:panier-calme', this.#kjCalme);
    document.addEventListener('kj:optimiste-ligne', this.#kjLigneAuClic);
  }

  /* KJ-B2B (12/10, panier rapide) : retrait ou quantité changés dans le tiroir / la page panier ({ key, quantity }) :
     la carte de la grille suit au clic (avant : à la réponse, ≈ 3 s). Affichage seul : la réponse du tiroir confirme. */
  #kjLigneAuClic = (event) => {
    const d = event.detail;
    if (!this.kjRow || !d || !d.key || d.key !== this.kjRow.dataset.lineKey) return;
    const q = Math.max(0, parseInt(d.quantity, 10) || 0);
    this.kjRow.classList.toggle('is-in-cart', q > 0);
    if (this.kjQty && String(this.kjQty.value) !== String(q)) this.kjQty.value = q;
  };

  /* KJ-B2B (12/10, panier rapide) : autres cartes cliquées entre-temps pour une NOUVELLE ligne (pas encore au panier),
     dont la requête attend dans la file. Elles partent avec la mienne, dans un seul add.js à plusieurs lignes : 3 recettes
     ajoutées vite = 2 requêtes au lieu de 3 (la 1re part tout de suite, les suivantes ensemble). Une variante par envoi. */
  #kjAutresEnAttente() {
    const vus = new Set([String(this.addToCartButton?.dataset.variantId || '')]);
    const out = [];
    document.querySelectorAll('quick-add-component').forEach((c) => {
      if (c === this || !c.kjRow || !c.kjEnCours || c.kjAffichee == null || c.kjRow.dataset.lineKey) return;
      const variantId = String(c.addToCartButton?.dataset.variantId || '');
      const ajout = c.kjAffichee - (parseInt(c.kjRow.dataset.qty, 10) || 0);
      if (!variantId || ajout <= 0 || vus.has(variantId)) return;
      vus.add(variantId);
      out.push({ carte: c, variantId, ajout });
    });
    return out;
  }

  /* KJ-B2B (12/10) : carte servie par l'envoi groupé d'une autre : on pose la vérité (quantité, clé) sans toucher à
     l'affichage ; sa propre requête, quand son tour vient, voit qu'elle est servie et ne part pas (ou envoie la suite). */
  #kjServir(qty, key) {
    if (!this.kjRow) return;
    this.kjRow.dataset.qty = Math.max(0, parseInt(qty, 10) || 0);
    this.kjRow.dataset.lineKey = key || '';
  }

  /* KJ-B2B (09/10, panier rapide) : affichage optimiste. La carte, le liseré et le compteur « Ma sélection » bougent
     au clic ; la requête part derrière (file unique) et le serveur confirme. Vérité : kjRow.dataset.qty / lineKey.
     Affiché : kjAffichee (null = rien en attente, on affiche la vérité). */
  #kjAfficher(qty) {
    if (!this.kjRow) return;
    const avant = this.kjAffichee ?? (parseInt(this.kjRow.dataset.qty, 10) || 0);
    qty = Math.max(0, qty);
    this.kjAffichee = qty;
    this.kjRow.classList.toggle('is-in-cart', qty > 0);
    if (this.kjQty && String(this.kjQty.value) !== String(qty)) this.kjQty.value = qty;
    this.kjQty?.closest('quantity-selector')?.querySelector('button[name="minus"]')?.removeAttribute('disabled');
    if (avant !== qty) {
      document.dispatchEvent(new CustomEvent('kj:optimiste', {
        detail: { variantId: this.addToCartButton?.dataset.variantId, avant, apres: qty, carte: this.closest('product-card') },
      }));
    }
  }

  /* KJ-B2B (09/10) : sur la page panier, on remplace son composant par la version de la réponse (pas de rechargement) */
  #kjPagePanier(sections) {
    const comp = document.querySelector('cart-component');
    if (!comp) return true;
    const sec = comp.closest('.shopify-section');
    const html = sec && sections?.[sec.id.replace('shopify-section-', '')];
    const neuf = html && new DOMParser().parseFromString(html, 'text/html').querySelector('cart-component');
    if (!neuf) return false;
    comp.replaceWith(neuf);
    return true;
  }

  /* KJ-B2B : état de la ligne (quantité, clé) reflété sur la carte */
  #kjSet(qty, key) {
    if (!this.kjRow) return;
    qty = Math.max(0, parseInt(qty, 10) || 0);
    this.kjRow.dataset.qty = qty;
    this.kjRow.dataset.lineKey = key || '';
    // Des clics sont encore en file : on garde l'affichage optimiste (pas de retour en arrière 3 → 1 → 2 → 3).
    if ((this.kjEnCours || 0) > 1) return;
    this.kjAffichee = null;
    this.kjRow.classList.toggle('is-in-cart', qty > 0);
    if (this.kjQty && String(this.kjQty.value) !== String(qty)) this.kjQty.value = qty;
    this.kjQty?.closest('quantity-selector')?.querySelector('button[name="minus"]')?.removeAttribute('disabled');
  }

  /* KJ-B2B : − / + ou saisie dans le sélecteur → change.js sur la ligne payante (add.js si elle n'existe plus) */
  #kjHandleQtyChange = async () => {
    if (!this.kjRow) return;
    this.kjEnCours = (this.kjEnCours || 0) + 1;
    this.#kjAfficher(Math.max(0, parseInt(this.kjQty.value, 10) || 0));
    try {
      await kjFile(async () => {
        // Lu au moment où la requête part : la dernière valeur saisie gagne, des clics rapides se fondent en une requête.
        const voulu = Math.max(0, parseInt(this.kjQty.value, 10) || 0);
        const avant = parseInt(this.kjRow.dataset.qty, 10) || 0;
        if (String(this.kjQty.value) !== String(voulu)) this.kjQty.value = voulu;
        if (voulu === avant) return;
        const key = this.kjRow.dataset.lineKey;
        if (key) {
          const data = await this.#kjPost('/cart/change.js', { id: key, quantity: voulu });
          const ligne = data.items?.find((i) => i.key === key);
          this.#kjSet(ligne ? ligne.quantity : 0, ligne ? ligne.key : '');
          this.#kjNotifier(data, voulu - avant, data);
        } else if (voulu > 0) {
          const data = await this.#kjPost(Theme.routes.cart_add_url, {
            items: [{ id: this.addToCartButton.dataset.variantId, quantity: voulu }],
          });
          const ligne = data.items?.[0];
          this.#kjSet(ligne ? ligne.quantity : voulu, ligne ? ligne.key : '');
          this.#kjNotifier(data, voulu - avant, null, ligne);
        }
      });
    } catch (error) {
      console.error(error);
      this.kjAffichee = null; // le recalage au calme (erreur signalée) remet la carte et le compteur sur le panier réel
      window.kjPanierFile?.signaler();
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
    } finally {
      this.kjEnCours -= 1;
      if (!this.kjEnCours) this.kjAffichee = null;
    }
  };

  /* KJ-B2B : un seul appel panier qui ramène le tiroir et les blocs compteur de la page */
  async #kjPost(route, body) {
    if (window.kjPanierFile?.ecrire) return window.kjPanierFile.ecrire(route, body, ['auto-gifts']);
    const sections = ['auto-gifts'];
    const response = await fetch(route, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...body, sections: [...new Set(sections)].join(','), sections_url: window.location.pathname }),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.description || data.message || 'Erreur panier.');
    return data;
  }

  /* KJ-B2B : événement natif ; si la réponse est le panier complet, les autres cartes le lisent sans requête */
  #kjNotifier(data, delta, panier, ligne) {
    const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
    const event = new CartUpdatedEvent(
      {},
      {
        itemCount: currentItemCount != null ? Math.max(0, currentItemCount + delta) : undefined,
        source: 'quick-add',
        sections: data?.sections,
      },
    );
    if (panier?.items) {
      event.kjPanier = Promise.resolve(panier);
      event.kjFrais = true;
    }
    // KJ-B2B (09/10) : ajout d'une carte (add.js, pas de panier complet) : seules les cartes de la même variante
    // ont à suivre, et elles lisent la ligne ici. Avant : une lecture /cart.js de plus, jetée comme périmée.
    event.kjCarte = { variantId: String(this.addToCartButton?.dataset.variantId || ''), ligne: ligne || null };
    if (!this.#kjPagePanier(data?.sections)) {
      window.location.reload();
      return;
    }
    document.dispatchEvent(event);
  }

  /* KJ-B2B : après tout changement du panier (tiroir, page panier, panneau, autre carte), la carte se resynchronise.
     Un seul /cart.js par événement, partagé entre toutes les cartes de la page. */
  async #kjSync(event) {
    if (!this.kjRow || this.kjEnCours) return;
    if (event.kjCarte && !event.kjPanier) {
      const moi = String(this.addToCartButton?.dataset.variantId || '');
      if (event.kjCarte.variantId !== moi) return; // l'ajout d'une autre recette ne change pas ma ligne
      const l = event.kjCarte.ligne;
      if (l) this.#kjSet(l.quantity, l.key);
      return;
    }
    const tour = window.kjPanierFile?.tour;
    if (!event.kjPanier) event.kjPanier = fetch('/cart.js').then((r) => r.json());
    try {
      const panier = await event.kjPanier;
      // Une écriture s'est terminée pendant la lecture : cette réponse est périmée, la suivante fera foi.
      if (this.kjEnCours || (window.kjPanierFile && window.kjPanierFile.tour !== tour && !event.kjFrais)) return;
      this.#kjAppliquer(panier);
    } catch (e) {
      console.warn('quick-add : synchronisation panier impossible', e);
    }
  }

  /* KJ-B2B (29/09) : la carte reflète la ligne payante de sa variante dans un panier complet */
  #kjAppliquer(panier) {
    const variantId = String(this.addToCartButton?.dataset.variantId || '');
    const ligne = panier.items?.find((i) => String(i.variant_id) === variantId && i.properties?._offert !== '1');
    this.#kjSet(ligne ? ligne.quantity : 0, ligne ? ligne.key : '');
  }

  /* KJ-B2B (29/09) : la file s'est vidée, chaque carte se recale sur le panier réel (une seule lecture pour toutes) */
  #kjCalme = async (event) => {
    if (!this.kjRow || this.kjEnCours) return;
    if (!event.kjPanier) event.kjPanier = fetch('/cart.js').then((r) => r.json());
    try {
      this.#kjAppliquer(await event.kjPanier);
    } catch (e) {}
  };

  disconnectedCallback() {
    this.addToCartButton?.removeEventListener('click', this.#handleAddToCart);
    this.popupToggle?.removeEventListener('click', this.#handlePopupToggle);
    this.popupModal?.removeEventListener('close', this.#handlePopupClose);
    this.kjQty?.removeEventListener('change', this.#kjHandleQtyChange);

    // KJ-B2B (09/10) : avant, une fonction anonyme différente : l'écouteur n'était jamais retiré.
    document.removeEventListener(ThemeEvents.cartUpdated, this.#handleCartUpdate);
    document.removeEventListener('kj:panier-calme', this.#kjCalme);
    document.removeEventListener('kj:optimiste-ligne', this.#kjLigneAuClic);
  }

  #isGiftOnly() {
    const productId = this.dataset.productId;
    if (!productId) return false;

    const giftOnlyEntry = window.__giftOnlyProducts?.[productId];
    if (!giftOnlyEntry) return false;

    const button = this.addToCartButton || this.popupToggle;
    if (button) {
      button.disabled = true;

      const contentEl = button.querySelector('.button__content');
      if (contentEl) contentEl.textContent = giftOnlyEntry.message || '';
    }

    return true;
  }

  #handleAddToCart = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    /* KJ-B2B (29/09, entrée 252) : chaque clic = +1 colis, mis dans la file unique ; la ligne et la quantité sont
       lues au moment où la requête part (plus au moment du clic), donc des clics rapides donnent le bon total. */
    this.kjEnCours = (this.kjEnCours || 0) + 1;
    // KJ-B2B (09/10, panier rapide) : +1 affiché au clic (sélecteur, liseré, compteur) ; plus de roue qui bloque le bouton.
    if (this.kjRow) this.#kjAfficher((this.kjAffichee ?? (parseInt(this.kjRow.dataset.qty, 10) || 0)) + 1);
    else this.addToCartButton.classList.add('is-loading');
    try {
      await kjFile(async () => {
        const avant = parseInt(this.kjRow?.dataset.qty, 10) || 0;
        const key = this.kjRow?.dataset.lineKey;
        // KJ-B2B (09/10, panier rapide) : une rafale = une requête. On envoie la quantité affichée (voulue) au moment
        // où la requête part ; les clics suivants de la rafale, déjà servis, ne partent pas.
        const voulu = this.kjRow ? (this.kjAffichee ?? avant + 1) : avant + 1;
        if (voulu <= avant) return;
        let data = null;
        if (key) {
          try {
            data = await this.#kjPost('/cart/change.js', { id: key, quantity: voulu });
          } catch (e) {
            data = null; // ligne retirée entre-temps (tiroir, autre onglet) : on repart d'un ajout
          }
        }
        if (data) {
          const ligne = data.items?.find((i) => i.key === key);
          this.#kjSet(ligne ? ligne.quantity : voulu, ligne ? ligne.key : key);
          this.#kjNotifier(data, voulu - avant, data);
        } else {
          const ajout = key ? voulu : voulu - avant; // ligne disparue : on repart de zéro
          // KJ-B2B (12/10, panier rapide) : les autres recettes cliquées entre-temps partent dans la même requête.
          const groupe = key ? [] : this.#kjAutresEnAttente();
          data = await this.#kjPost(Theme.routes.cart_add_url, {
            items: [
              { id: this.addToCartButton.dataset.variantId, quantity: ajout },
              ...groupe.map((g) => ({ id: g.variantId, quantity: g.ajout })),
            ],
          });
          const trouver = (vid) => data.items?.find((i) => String(i.variant_id) === String(vid) && !i.properties?._offert);
          const ligne = trouver(this.addToCartButton.dataset.variantId) || data.items?.[0];
          this.#kjSet(ligne ? ligne.quantity : voulu, ligne ? ligne.key : '');
          groupe.forEach((g) => {
            const l = trouver(g.variantId);
            if (l) g.carte.#kjServir(l.quantity, l.key);
          });
          this.#kjNotifier(data, ajout + groupe.reduce((n, g) => n + g.ajout, 0), null, ligne);
        }
      });

      const addToCartBehavior = document.querySelector('cart-icon')?.addToCartBehavior;
      if (addToCartBehavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
          }),
        );
      }
    } catch (error) {
      console.error('Error:', error);
      this.kjAffichee = null;
      window.kjPanierFile?.signaler();
      document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: error.message } }));
    } finally {
      this.kjEnCours -= 1;
      if (!this.kjEnCours) {
        this.addToCartButton.classList.remove('is-loading');
        this.kjAffichee = null; // rafale finie : la vérité du panier reprend la main
      }
    }
  };

  updateCart(data, itemsAdded) {
    if (window.location.pathname.includes('/cart')) {
      window.location.reload();
    } else {
      const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();

      let newItemCount;
      if (currentItemCount != null) {
        newItemCount = currentItemCount + itemsAdded;
      }

      document.dispatchEvent(
        new CartUpdatedEvent(
          {},
          {
            itemCount: newItemCount,
            source: 'quick-add',
            sections: data.sections,
          },
        ),
      );

      const addToCartBehavior = document.querySelector('cart-icon')?.addToCartBehavior;
      if (addToCartBehavior === 'notification') {
        document.dispatchEvent(
          new CustomEvent('toast:open', {
            detail: { type: 'success', message: Theme.translations.addToCartNotificationMessage },
          }),
        );
      }
    }
  }

  #handlePopupToggle = async (event) => {
    this.popupModalContent.classList.add('is-loading');
    event.preventDefault();
    event.stopPropagation();

    this.#disableParentSliderDrag();

    const productPageHtml = await this.fetchProductPage(this.popupToggle.dataset.productPageUrl);

    if (productPageHtml) {
      const sourceRef = this.showProductMediaGallery === 'false' ? 'product-section-product-info' : 'product-section-content';
      this.popupModalContent.innerHTML = productPageHtml.querySelector(`[data-ref="${sourceRef}"]`).innerHTML;
      this.#activateScripts(this.popupModalContent);
    }

    // Notifier le product-form que le contenu est chargé pour déclencher la mise à jour des prix
    const productForm = this.popupModalContent.querySelector('[data-ref="product-form"]');
    productForm?.dispatchEvent(new CustomEvent('quickAddContentLoaded'));

    this.popupModalContent.classList.remove('is-loading');
  };

  #activateScripts(container) {
    for (const script of container.querySelectorAll('script')) {
      const activeScript = document.createElement('script');

      for (const attr of script.attributes) {
        activeScript.setAttribute(attr.name, attr.value);
      }

      activeScript.textContent = script.textContent;
      script.replaceWith(activeScript);
    }
  }

  #handlePopupClose = () => {
    this.#enableParentSliderDrag();
    this.popupModalContent?.replaceChildren();
  };

  #disableParentSliderDrag() {
    if (!this.#parentSliderComponent?.slider) return;
    this.#parentSliderComponent.slider.options = { drag: false };
  }

  #enableParentSliderDrag() {
    if (!this.#parentSliderComponent?.slider) return;
    this.#parentSliderComponent.slider.options = { drag: true };
  }

  #handleCartUpdate = (event) => {
    this.popupComponent?.close();
    if (event) this.#kjSync(event);
  };


  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

/* K&J (29/09/2026, entrée 252) : si une écriture de la rafale a échoué, tout se recale sur le panier réel quand
   la file se vide : tiroir, compteurs et cartes relus en une passe (sections + /cart.js). */
if (!window.kjRecalagePose) {
  window.kjRecalagePose = true;
  document.addEventListener('kj:panier-calme', async (e) => {
    if (!e.detail?.erreur) return;
    try {
      const ids = [];
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.dataset.sectionId) ids.push(drawer.dataset.sectionId);
      document.querySelectorAll('kj-palier-compteur').forEach((el) => {
        const section = el.closest('.shopify-section');
        if (section) ids.push(section.id.replace('shopify-section-', ''));
      });
      const url = new URL(window.location.href);
      url.searchParams.set('sections', [...new Set(ids)].join(','));
      const [sections, panier] = await Promise.all([
        ids.length ? fetch(url.toString()).then((r) => r.json()) : Promise.resolve({}),
        fetch('/cart.js').then((r) => r.json()),
      ]);
      const ev = new CartUpdatedEvent({}, { itemCount: panier.item_count, source: 'kj-recalage', sections });
      ev.kjPanier = Promise.resolve(panier);
      ev.kjFrais = true;
      document.dispatchEvent(ev);
    } catch (err) {
      console.warn('kj : recalage du panier impossible', err);
    }
  });
}
